import React, { useState, useEffect } from 'react';
import { Button, Spinner, Text, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { EmailData } from '@shared/types/email';
import { Task } from '@shared/types/tasks';
import { apiService } from '../services/apiService';

interface TasksPanelProps {
  email: EmailData | null;
  /** When false, no API calls (getAvailableTasks, executeTask) are made. */
  tasksEnabled?: boolean;
}

const TasksPanel: React.FC<TasksPanelProps> = ({ email, tasksEnabled = true }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadTasks = async () => {
    if (!tasksEnabled) return;
    try {
      setLoading(true);
      setError(null);
      const availableTasks = await apiService.getAvailableTasks();
      setTasks(availableTasks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const executeTask = async (taskId: string) => {
    if (!tasksEnabled || !email?.id) return;
    try {
      setExecuting(taskId);
      setError(null);
      setSuccess(null);

      const result = await apiService.executeTask({
        taskId,
        emailId: email.id,
        includeAttachments: true,
      });

      if (result.status === 'success') {
        setSuccess(`Task "${taskId}" executed successfully!`);
      } else {
        setError(result.errorMessage || 'Task execution failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to execute task');
    } finally {
      setExecuting(null);
    }
  };

  useEffect(() => {
    if (tasksEnabled) {
      loadTasks();
    } else {
      setTasks([]);
      setError(null);
    }
  }, [tasksEnabled]);

  useEffect(() => {
    setSuccess(null);
  }, [email?.id]);

  if (!tasksEnabled) {
    return (
      <div>
        <Text>Tasks are disabled. Enable Tasks under Settings → Automation to use tasks.</Text>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spinner label="Loading available tasks..." />
      </div>
    );
  }

  if (error && !tasks.length) {
    return (
      <div>
        <Text>{error}</Text>
        <Button onClick={loadTasks} style={{ marginTop: '10px' }}>
          Retry
        </Button>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div>
        <Text>No tasks available.</Text>
      </div>
    );
  }

  const hasEmail = Boolean(email?.id);

  return (
    <div>
      {success && (
        <MessageBar intent="success" style={{ marginBottom: '16px' }}>
          <MessageBarBody>{success}</MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent="error" style={{ marginBottom: '16px' }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <Text weight="semibold" size={400} style={{ display: 'block', marginBottom: '12px' }}>
        Available Tasks
      </Text>

      {tasks.map((task) => (
        <div key={task.id} style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e1dfdd', borderRadius: '4px' }}>
          <Text weight="semibold" size={300} style={{ display: 'block', marginBottom: '4px' }}>
            {task.name}
          </Text>
          {task.description && (
            <Text size={200} style={{ display: 'block', marginBottom: '8px', color: '#666' }}>
              {task.description}
            </Text>
          )}
          <Button
            appearance="primary"
            onClick={() => executeTask(task.id)}
            disabled={!hasEmail || executing === task.id || !task.enabled}
            style={{ marginTop: '8px' }}
          >
            {executing === task.id ? (
              <>
                <Spinner size="tiny" style={{ marginRight: '8px' }} />
                Executing...
              </>
            ) : (
              'Execute'
            )}
          </Button>
        </div>
      ))}
    </div>
  );
};

export default TasksPanel;
